import { ArrowRightIcon } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import { useEffect, useState } from 'react'

import { useEmbeddedOnboarding } from 'components/hooks/employer_onboarding/useEmbeddedOnboarding'
import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { Button, usePageTitle } from 'slab'

import { CenteredLayout } from '../CenteredLayout'
import { emitEmployerOnboardingEvent } from '../utils'

const MotionButtons = motion.create(CenteredLayout.Buttons)

interface HowItWorksPageProps {
  assets?: {
    images?: Record<string, string>
  }
}

export function HowItWorksPage ({ assets }: HowItWorksPageProps) {
  usePageTitle('How Thatch works')

  const { isEmbedded } = useEmbeddedOnboarding()
  // Skip the first step in embedded onboarding
  const [step, setStep] = useState(isEmbedded ? 2 : 1)
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()

  const withThatchGraphic = assets?.images?.['with-thatch-graphic'] ?? ''
  const withoutThatchGraphic = assets?.images?.['without-thatch-graphic'] ?? ''

  // Preload images to minimize the risk of flickering when transitioning between steps
  useEffect(() => {
    const images = [withThatchGraphic, withoutThatchGraphic]

    images.forEach((image) => {
      const img = new Image()
      img.src = image
      img.onload = () => {
        img.remove()
      }
    })
  }, [withThatchGraphic, withoutThatchGraphic])

  function renderHeadings () {
    switch (step) {
      case 1:
        return (
          <>
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className='heading-lg xs:heading-xl mb-4 text-balance'
            >
              Everyone has unique healthcare needs
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className='body xs:heading-sm xs:font-normal text-gray-500 text-balance'
            >
              Here's how Thatch works to accommodate for what your team members
              need.
            </motion.p>
          </>
        )
      case 2:
        return (
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='heading-lg xs:heading-xl text-balance mb-10'
          >
            Instead of choosing 1 insurance plan to offer your entire team...
          </motion.h1>
        )
      case 3:
        return (
          <motion.h1
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='heading-lg xs:heading-xl text-balance mb-10'
          >
            ...each employee gets a monthly allowance to spend as they’d like!
          </motion.h1>
        )
    }
  }

  function renderGraphic () {
    if (step !== 1) {
      return (
        <AnimatePresence mode='popLayout'>
          <motion.img
            aria-hidden
            width={1076}
            height={648}
            key={step}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            src={step === 2 ? withoutThatchGraphic : withThatchGraphic}
            className='block w-full h-auto'
          />
        </AnimatePresence>
      )
    }
  }

  function renderButtons () {
    const previousPageButton = (
      <Button variant='secondary' onClick={navigateToPreviousStep}>
        Back
      </Button>
    )
    const previousStepButton = (
      <Button variant='secondary' onClick={() => setStep((prev) => prev - 1)}>
        Back
      </Button>
    )
    const nextStepButton = (
      <Button
        variant='secondary'
        onClick={() => {
          if (step === 1) {
            emitEmployerOnboardingEvent('[ER] how it works next 1 clicked', {})
          }
          if (step === 2) {
            emitEmployerOnboardingEvent('[ER] how it works next 2 clicked', {})
          }
          setStep((prev) => prev + 1)
        }}
      >
        Next
      </Button>
    )
    const nextPageButton = (
      <Button
        variant='primary'
        onClick={() => {
          navigateToNextStep()
          emitEmployerOnboardingEvent('[ER] how it works next 3 clicked', {})
        }}
        size='large'
        rightIcon={ArrowRightIcon}
      >
        Sounds great!
      </Button>
    )

    switch (step) {
      case 1:
        return (
          <>
            {previousPageButton}
            {nextStepButton}
          </>
        )
      case 2:
        return (
          <>
            {previousStepButton}
            {nextStepButton}
          </>
        )
      case 3:
        return (
          <>
            {previousStepButton}
            {nextPageButton}
          </>
        )
    }
  }

  return (
    <CenteredLayout displaySupportLink verticallyCentered>
      <div className='text-center mb-16'>
        {renderHeadings()}
        {renderGraphic()}
      </div>
      <MotionButtons initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        {renderButtons()}
      </MotionButtons>
    </CenteredLayout>
  )
}
